import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useEmbedStore } from '@/stores/embed-store';
import { initiateLoginRedirect } from '@/lib/oauth';
import { captureAndPersistRemix } from '@/lib/remix-intent';
import EmbedOnboarding from '@/embed/EmbedOnboarding';

const USE_DIRECT_AUTH = import.meta.env.VITE_USE_DIRECT_AUTH === 'true';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isEmbedded = useEmbedStore((s) => s.isEmbedded);
  const needsAdminAccess = useEmbedStore((s) => s.needsAdminAccess);
  const needsProvisioning = useEmbedStore((s) => s.needsProvisioning);

  // If not authenticated, redirect to login page or OptiPrime
  useEffect(() => {
    // In embed mode, don't redirect — wait for SYNC_AUTH from parent
    if (isEmbedded) return;

    if (!loading && !user) {
      // Persist any ?remix=<appId> deep-link intent before the OAuth/signup bounce
      // (returnUrl carries it too, but signup → onboarding can drop query params).
      captureAndPersistRemix();

      // Capture current URL to return to after authentication
      const returnUrl = `${window.location.pathname}${window.location.search}`;

      if (USE_DIRECT_AUTH) {
        // Redirect to local login page with return_url
        const encodedReturnUrl = encodeURIComponent(returnUrl);
        navigate(`/auth/login?return_url=${encodedReturnUrl}`);
      } else {
        // Redirect to OptiPrime OAuth with return_url stored in sessionStorage
        initiateLoginRedirect(returnUrl);
      }
    }
  }, [loading, user, navigate, isEmbedded]);

  // Embed user whose OptiSigns role is below the sync threshold — render the same
  // onboarding UI as admins. Submit will surface an admin-access request modal
  // instead of starting a trial (handled inside EmbedOnboarding).
  if (isEmbedded && needsAdminAccess) {
    return <EmbedOnboarding />;
  }

  // Admin/owner in embed mode with no workspace yet — show onboarding to provision one.
  // We do NOT require `user` here: with the auto-fired /api/auth/join removed (it used
  // to JIT-create a junk personal workspace), AuthContext is only populated AFTER the
  // user clicks "Build it" and PROVISION_WORKSPACE → /api/workspaces/provision creates
  // the real workspace. EmbedOnboarding itself doesn't read `user`, so rendering it
  // pre-auth is safe — same pattern as the needsAdminAccess branch above.
  if (isEmbedded && needsProvisioning) {
    return <EmbedOnboarding />;
  }

  if (loading || (isEmbedded && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    // Show minimal loading state while redirect is happening
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
}
